import React, { useEffect, useMemo, useState } from 'react';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import useSWR, { useSWRConfig } from 'swr';
import { ArrowDown, ArrowUp, Loader2, Plus, Search, Settings2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useT } from '@/contexts/I18nContext';
import { usePageTitle } from '@/contexts/PageHeaderContext';
import { listZones, createZone, type ZoneDto } from '@/api/zones';
import { listLocalComputers } from '@/api/localComputers';
import { useOpenZone } from '@/hooks/useOpenZone';
import { ZONES_SWR_KEY } from '@/hooks/useCurrentZone';
import { cn } from '@/lib/utils';

// Система → Филиалы: the superuser home for the branch inventory. Branch
// creation lives HERE (moved out of the header branch-selector dropdown —
// Alexey, 2026-07-19); the table lists every zone including archived ones.

export default function BranchesListPage() {
  const { t } = useT();
  usePageTitle(t('admin.branches.title'), t('admin.branches.subtitle'));

  const [query, setQuery] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const { mutate: globalMutate } = useSWRConfig();

  const { data, error, isLoading, mutate } = useSWR('admin-branches', () =>
    listZones(1000, true).then(r => r.data.results),
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = data ?? [];
    // Ordering lives in BranchesTable (clickable column sort).
    return q ? rows.filter(z => (z.humanreadable_name || '').toLowerCase().includes(q)) : rows;
  }, [data, query]);

  const handleCreated = async () => {
    await mutate();
    // The header selector shares the zones cache — refresh it so the new
    // branch appears there without a reload.
    await globalMutate(ZONES_SWR_KEY);
  };

  return (
    <div className="container mx-auto max-w-5xl space-y-4 px-4 py-6">
      <Card variant="section">
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="relative max-w-md flex-1">
              <Search className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={t('admin.branches.searchPlaceholder')}
                className="h-9 w-full rounded-md border bg-background pl-8 pr-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:border-ring"
                autoComplete="off"
              />
            </div>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" />
              {t('branchSelector.create')}
            </Button>
          </div>

          {error && !data && <ServerStatusNotice variant="card" error={error} onRetry={() => mutate()} />}

          <BranchesTable rows={filtered} loading={isLoading} />

          <div className="text-xs text-muted-foreground">
            {(data?.length ?? 0) > 0 && t('admin.branches.total', { n: filtered.length })}
          </div>
        </CardContent>
      </Card>

      <CreateBranchDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
      />
    </div>
  );
}

type SortKey = 'name' | 'type' | 'timezone' | 'capture' | 'status';
type SortDir = 'asc' | 'desc';

function BranchesTable({ rows, loading }: { rows: ZoneDto[]; loading: boolean }) {
  const { t } = useT();
  const openZone = useOpenZone();
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // One fetch for the whole table: Zone.deployment matching some
  // LocalComputer.deployment_key ⇒ on-site computer capture; else
  // direct/router capture. Undefined while loading ⇒ render '—'.
  const { data: lcs } = useSWR('admin-branches-lcs', () =>
    listLocalComputers({ limit: 500 }).then(r => r.data.results),
  );
  const lcKeys = useMemo(() => {
    const s = new Set<string>();
    for (const lc of lcs ?? []) if (lc.deployment_key) s.add(lc.deployment_key);
    return s;
  }, [lcs]);

  // Some DB rows carry lowercase types ('shop') — normalize before the
  // dictionary lookup; t() echoes the key back on a miss, so compare to
  // detect it and fall back to the raw DB value.
  const typeLabel = (z: ZoneDto): string => {
    if (!z.type) return '';
    const key = `general.zoneType.${z.type.toUpperCase()}`;
    const label = t(key as Parameters<typeof t>[0]);
    return label === key ? z.type : label;
  };

  const isLcCapture = (z: ZoneDto): boolean =>
    Boolean(z.deployment && lcKeys.has(z.deployment));

  const sorted = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    const value = (z: ZoneDto): string | number => {
      switch (sortKey) {
        case 'name': return (z.humanreadable_name || '').toLowerCase();
        case 'type': return typeLabel(z).toLowerCase();
        case 'timezone': return z.timezone || '';
        case 'capture': return isLcCapture(z) ? 0 : 1;
        case 'status': return z.is_archived ? 1 : 0;
      }
    };
    return rows.slice().sort((a, b) => {
      // Invariant: archived branches always sink below active ones,
      // whatever column/direction is selected.
      const archived = Number(!!a.is_archived) - Number(!!b.is_archived);
      if (archived !== 0) return archived;
      const va = value(a);
      const vb = value(b);
      const cmp = typeof va === 'number' && typeof vb === 'number'
        ? va - vb
        : String(va).localeCompare(String(vb));
      if (cmp !== 0) return cmp * dir;
      // Stable tiebreaker: name asc.
      return (a.humanreadable_name || '').localeCompare(b.humanreadable_name || '');
    });
  }, [rows, sortKey, sortDir, lcKeys, t]);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const columns: Array<{ key: SortKey | 'actions'; label: string }> = [
    { key: 'name', label: t('admin.branches.colName') },
    { key: 'type', label: t('admin.branches.colType') },
    { key: 'timezone', label: t('admin.branches.colTimezone') },
    { key: 'capture', label: t('admin.branches.colCapture') },
    { key: 'status', label: t('admin.branches.colStatus') },
    { key: 'actions', label: '' },
  ];

  const open = (z: ZoneDto) => openZone(String(z.id), '/management/general');

  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            {columns.map(c => (
              <th key={c.key} className="px-3 py-2 text-left font-semibold">
                {c.key === 'actions' ? c.label : (
                  <button
                    type="button"
                    onClick={() => toggleSort(c.key as SortKey)}
                    className={cn(
                      'inline-flex items-center gap-1 uppercase tracking-wider outline-none',
                      'hover:text-foreground focus-visible:text-foreground',
                      sortKey === c.key && 'text-foreground',
                    )}
                    aria-sort={sortKey === c.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                  >
                    {c.label}
                    {sortKey === c.key && (
                      sortDir === 'asc'
                        ? <ArrowUp className="size-3" />
                        : <ArrowDown className="size-3" />
                    )}
                  </button>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && sorted.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="px-3 py-6 text-center text-muted-foreground">
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="size-4 animate-spin" /> {t('common.loading')}
                </span>
              </td>
            </tr>
          )}
          {!loading && sorted.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="px-3 py-6 text-center text-muted-foreground">
                {t('admin.branches.empty')}
              </td>
            </tr>
          )}
          {sorted.map(z => (
            <tr key={z.id} className="border-t hover:bg-muted/30">
              <td className="px-3 py-2 font-medium">
                {/* useOpenZone (not a plain <Link>): selects the branch in app
                    state so the target page shows THIS branch instead of the
                    lingering «Все филиалы» portfolio mode. */}
                <button
                  type="button"
                  onClick={() => open(z)}
                  className="text-left hover:underline focus-visible:underline outline-none"
                >
                  {z.humanreadable_name || `#${z.id}`}
                </button>
              </td>
              <td className="px-3 py-2 text-muted-foreground">{typeLabel(z) || '—'}</td>
              <td className="px-3 py-2 text-muted-foreground">{z.timezone || '—'}</td>
              <td className="px-3 py-2">
                {lcs === undefined
                  ? <span className="text-muted-foreground">—</span>
                  : (
                    <Badge variant="muted">
                      {isLcCapture(z)
                        ? t('admin.branches.captureLc')
                        : t('admin.branches.captureRouter')}
                    </Badge>
                  )}
              </td>
              <td className="px-3 py-2">
                {z.is_archived
                  ? <Badge variant="muted">{t('admin.branches.archived')}</Badge>
                  : <Badge variant="success">{t('admin.branches.active')}</Badge>}
              </td>
              <td className="px-3 py-2 text-right">
                <Button variant="ghost" size="sm" onClick={() => open(z)}>
                  <Settings2 className="size-3.5" />
                  {t('admin.branches.openSettings')}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Moved verbatim from BranchSelector (its former create-branch flow).
function CreateBranchDialog({
  open, onClose, onCreated,
}: { open: boolean; onClose: () => void; onCreated: (id: string) => void }) {
  const { t } = useT();
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) { setName(''); setError(null); }
  }, [open]);

  const handleCreate = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      const resp = await createZone(name.trim());
      onCreated(String(resp.data.id));
      onClose();
    } catch (err: any) {
      const data = err?.response?.data;
      setError(data?.humanreadable_name?.[0] ?? data?.detail ?? t('branchSelector.createFail'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="relative">
        <DialogHeader>
          <DialogTitle>{t('branchSelector.createTitle')}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label htmlFor="branch-name">{t('branchSelector.createNameLabel')}</Label>
          <Input
            id="branch-name"
            autoFocus
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
          />
          {error && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">{error}</div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={handleCreate} disabled={saving || !name.trim()}>
            {saving ? t('branchSelector.createSaving') : t('common.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
